import { AddressContract } from './address';
import { BundleLabels } from './bundleDevices';
import { CartridgeType, DeviceCartridgeState } from './cartridges';
import { DeviceEncash } from './encash';
import { LastSale } from './sales';
import { ServiceCode } from './services';

export type Device = {
  Id: string;
  Cartridges: Partial<Record<CartridgeType, DeviceCartridgeState>>;
  DeviceState: DeviceState;
  Settings: Settings;
  Info: Info;
  Username?: string;
  Features?: Features;
  Comment?: string;
};

export enum FiscalDevice {
  kz_fiscal_service = 'kz_fiscal_service',
  md_fiscal_printer = 'md_fiscal_printer'
}
export interface Features {
  IsTuneMainboard: boolean;
  HasTempSensor1: boolean;
  HasTempSensor2: boolean;
  IsTuneTempSensor2: boolean;
  IsControlV3?: boolean;
  HasWaterprepare?: boolean;
  FiscalDevice?: FiscalDevice;
  HasTempSensor3?: boolean;
  IsWaterprepareSettingsV2?: boolean;
  IsDeviceOnlineVersion: boolean;
  IsWifiOnboard?: boolean;
  IsBottlerOnboard?: boolean;
  IsWaterpepareCountersEditable?: boolean;
  IsWaterpepareAcidWash?: boolean;
}

export type DeviceState = {
  Balance?: number | string | null;
  Cnv?: string | number;
  DailySales: DailySales;
  Alerts?: Alert[];
  SceD?: number;
  SciD?: number;
  Prf?: number | null;
  Id?: string;
  BarrelVolume?: number;
  Door?: boolean | null;
  BillValidorStatus?: string | null;
  TubeMoney?: number | null;
  WaterOutput?: number;
  Banknotes?: number;
  Coins?: number;
  Lnk?: number;
  Link?: number;
  SignalLevel?: number;
  DiagDat?: boolean;
  idCard?: string;
  HighLevel?: boolean;
  TankEmpty?: boolean | null;
  TankFull?: boolean;
  Tmp?: number;
  Tmp2?: number | null;
  Tmp3?: number | null;
  Distance?: number | null;
  Lg1?: string;
  Lg2?: string;
  Csf?: number | null;
  /**
   * @deprecated Вместо используй Features.HasWaterprepare
   */
  Bvp?: number | null;
  BalanceString?: string;
  Phone?: string;
  Version?: string;
  TurnOnAt?: number;
  Lat?: string | number;
  Lng?: string | number;
  VersionDt?: number | null;
  Timestamp?: number;
  LastOnline?: number;
  UpdatedAt?: number;
  LastSale?: LastSale;
  Lamp?: boolean | null;
  HopMoney?: boolean;
  HopStatus?: string;
  Low?: boolean | null;
  Leakage?: boolean | null;
  TankMinLevel?: boolean | null;
  LastEncash?: DeviceEncash | null;
  MnIn?: number;
  MnOut?: number;
  Scp?: number;
  Scc?: number;
  Sci?: number;
  Dpm?: number;
  Dti?: number;
  Dtt?: number;
  Sfp?: number;
  Sfc?: number;
  Sce?: number;
  Tfl?: number;
  Pfl?: number;
  High?: unknown;
  Mineral?: unknown;
  WasteWater?: unknown;
  PouredOut?: unknown;
  WaterInput?: unknown;
  Fpc?: boolean;
  Fpe?: string;
  TotalBottles?: number;
  // версия платы Водоподготовки
  VersionWpc?: string;
};

export interface Alert {
  Code: AlertCode;
  Desc: string;
  Dt?: number | null;
  Error?: string | null;
  level?: number;
}

export enum AlertCode {
  Offline = 'Offline',
  WaterIsOver = 'WaterIsOver',
  WaterPressureLow = 'WaterPressureLow',
  DoorIsOpen = 'DoorIsOpen',
  NoSales2h = 'NoSales2h',
  NoBankcardSales2h = 'NoBankcardSales2h',
  ExchangeIsOver = 'ExchangeIsOver',
  SimBalanceLow = 'SimBalanceLow',
  TankMinLevel = 'TankMinLevel',
  FiscalError = 'FiscalError',
  BillValidatorError = 'BillValidatorError',
  HopperError = 'HopperError',
  RebootRequied = 'RebootRequired',
  Leakage = 'Leakage',
  ExternalPowerIsOff = 'ExternalPowerIsOff',
  HighTemperatureInputWater = 'HighTemperatureInputWater',
  InefficientFlushing = 'InefficientFlushing',
  UVlampDefective = 'UVlampDefective'
}

export interface DailySales {
  Sum: Sum;
  Sum_w0?: Sum;
  Sum_w1?: Sum;
  Sum_w16?: number;
}

export interface Sum {
  Amount: number;
  Litres: number;
  Banknotes?: number;
  Coins?: number;
  Bankcard?: number;
  MobileApp?: number;
  Exchange?: number;
}

export interface Info {
  UserId: string;
  Group?: string;
  DeviceKey: string;
  Address: string;
  PosLat?: number | string | null;
  PosLng?: number | string | null;
  CostPerLiter: number;
  CostPerLiterByCard: number;
  CostPerLiterMineral?: number;
  Currency: string;
  Timezone: string;
  Phone: string;
  SerialNumber?: string;
  SupportPhone: string;
  AddressContract?: AddressContract | Record<string, null>;
  LastService?: number | null;
  AuthKey?: string;
  SourceIP?: string;
  Blocks?: ServiceCode[];
  AllowMineralPayment?: boolean;
  CreatedAt?: number;
  IsActivated?: boolean;
}

export interface Settings {
  Prepare: { [key: string]: number };
  Main: Main | Record<string, null>;
  ReloadSettings: boolean;
  AllowBottling: boolean;
  DeviceType: DeviceType;
  BarrelMax?: number | string;
  ResetWaterOutput: boolean;
  RestartMoneyAcceptors: boolean;
  EnableMoneyAcceptors: boolean;
  MobileAppIncomeEnabled?: boolean;
  Gprs: GPRS;
  Notifications: Notifications;
  BarrelVolumeCorrect: number;
  FiscalService?: number;
  FiscalKkm?: string | null;
  FiscalLogin?: string | null;
  FiscalPassword?: string | null;
  FiscalRestore?: boolean | number | null;
  FiscalShiftNum?: number | string;
  FiscalShiftState?: string;
  FiscalShiftOpenDt?: string;
  FiscalError?: string;
  FiscalErrorDt?: number;
  FiscalServerStage?: string;
  EnableVoiceAssist?: boolean;
  EnablePromo?: boolean;
  EnableWaterprepare?: boolean;
  BacklightIntervalStart?: string; // если по расписанию - начало
  BacklightIntervalStop?: string; // если по расписанию - конец
  BacklightOption?: BacklightOption;
  SunCalcDate?: string;
  SunCalcSunrise?: string;
  SunCalcSunset?: string;
  // возможна связка с другими аппаратами
  Bundle?: IDeviceBundleSettings;
  // Флаг для перезагрузки платы управления
  RestartDevice?: boolean;
  // Архивная версия платформы
  MaintenancePlatform?: boolean;
}

// подсветка
export enum BacklightOption {
  Off = 0,
  Schedule = 1, // по расписанию
  SunCalc = 2 // по рассвету и закату
}

export enum DeviceType {
  Barrel = 'barrel',
  Filter = 'filter',
  Purifier = 'purifier'
}

export interface GPRS {
  UssdRequestBalance?: string | null;
  UssdRequestNumber?: string | null;
  BalanceRegex?: string | null;
  MobileOperator?: string | null;
}

export interface Notifications {
  EnableTankEmpty: boolean;
  SendEmailDailyReport: boolean;
  BalanceLimit?: number;
  EnableDoor?: boolean;
  EnableHigh?: boolean;
  EnableHopper?: boolean;
  EnableLamp?: boolean;
  EnableLeakage?: boolean;
  EnableLow?: boolean;
  EnableTankFull?: boolean;
  EnableTankMinLevel?: boolean;
  BankCardPayError?: boolean;
  BanknoteAcceptorError?: boolean;
  CoinAcceptorError?: boolean;
  ExchangeIsOver?: boolean;
  NoSales2h?: boolean;
  NoBankcardSales2h?: boolean;
  Offline?: boolean;
  WaterMeterError?: boolean;
  EnableHighTempInWater?: boolean;
  EnableExternalPowerSwitchOff?: boolean;
  EnableLowTemperatureSensor?: boolean;
}

type UUID = string;

export interface IDeviceBundleSettings {
  MainDeviceId?: UUID;
  SecondaryDeviceIds?: UUID[];
  DeviceLabel?: string;
  BundleLabels?: BundleLabels;
}

export interface PeripheralBottlerSettings {
  // Блокировка при открытой дверце
  Interlock: boolean;
  // Цена бутылки
  Price: number;
}

export interface Main {
  Kup: number;
  Mon: number;
  Sco: number;
  Prs: number;
  Prc: number;
  Prb: number;
  Val: number;
  Dgd: number;
  Otp: number;
  Idc: number;
  Nfc: number;
  Tmm: number;
  Tmx: number;
  Dsp: number;
  Ast: number;
  Sbb: number;
  Fex: number;
  Ozn: number;
  Uzd: number;
  V98: number;
  Asi: number;
  Bcs: number;
  Dzn: number;
  Bottler?: PeripheralBottlerSettings;
}

export type ActivateDeviceParams = {
  deviceId: string;
  isActivated: boolean;
};

export type IncomeDeviceParams = {
  deviceId: string;
  Sum: string;
  Comment?: string;
};

export interface UpdateDeviceMetersParams {
  deviceId: string;
  SceU?: number;
  SciU?: number;
  OutU?: number;
}
